import Box from '@mui/material/Box';
import { alpha, darken, useTheme } from '@mui/material/styles';
import { t } from 'i18next';

import {
  OnboardingCard,
  SecondaryText,
  StepCallout,
  TitleText,
} from '../CompanyOnboardingPrimitives';

const SHAPE_KEYS = ['circle', 'polygon', 'multiple'] as const;

const PRIMARY_FILL_ALPHA = 0.19;
const SECONDARY_FILL_ALPHA = 0.13;
const YELLOW_STROKE_DARKEN = 0.38;
const GREEN_STROKE_DARKEN = 0.28;

const ShapeSvg = ({ shapeKey }: { shapeKey: (typeof SHAPE_KEYS)[number] }) => {
  const theme = useTheme();
  const roadStroke = theme.palette.divider;
  const pinFill = theme.palette.text.primary;
  const { treadBlue, treadGreen, treadYellow } = theme.brandV2.colors;
  const yellowFill = alpha(treadYellow, PRIMARY_FILL_ALPHA);
  const yellowStroke = darken(treadYellow, YELLOW_STROKE_DARKEN);
  const blueFill = alpha(treadBlue, SECONDARY_FILL_ALPHA);
  const greenFill = alpha(treadGreen, SECONDARY_FILL_ALPHA);
  const greenStroke = darken(treadGreen, GREEN_STROKE_DARKEN);

  const hasRoad = shapeKey !== 'multiple';

  return (
    <svg
      aria-label={t(
        `company_onboarding.modules.sites.shape_guide.${shapeKey}.aria_label`,
      )}
      height={140}
      viewBox="0 0 160 85"
    >
      {hasRoad ? <path d="M0 60H160" stroke={roadStroke} strokeWidth={10} /> : null}
      {shapeKey === 'circle' ? (
        <>
          <circle
            cx={80}
            cy={40}
            fill={yellowFill}
            r={31}
            stroke={yellowStroke}
            strokeWidth={2}
          />
          <circle cx={80} cy={40} fill={pinFill} r={4} />
        </>
      ) : null}
      {shapeKey === 'polygon' ? (
        <path
          d="M30 16 104 12 133 42 104 49 95 70 35 55Z"
          fill={yellowFill}
          stroke={yellowStroke}
          strokeWidth={2}
        />
      ) : null}
      {shapeKey === 'multiple' ? (
        <>
          <circle
            cx={48}
            cy={42}
            fill={yellowFill}
            r={25}
            stroke={yellowStroke}
            strokeWidth={2}
          />
          <path d="M85 15H139V44H85Z" fill={blueFill} stroke={treadBlue} />
          <circle cx={111} cy={65} fill={greenFill} r={12} stroke={greenStroke} />
        </>
      ) : null}
    </svg>
  );
};

const GeofenceShapeGuide = () => (
  <Box sx={{ mb: 3 }}>
    <StepCallout
      body={t('company_onboarding.modules.sites.defaults_callout_body')}
      title={t('company_onboarding.modules.sites.defaults_callout_title')}
      severity="warning"
    />
    <Box sx={{ display: 'flex', gap: 2 }}>
      {SHAPE_KEYS.map((shapeKey) => (
        <OnboardingCard
          data-test-id={`company-onboarding-geofence-shape-${shapeKey}`}
          key={shapeKey}
          sx={{ flex: 1, p: 2, textAlign: 'center' }}
        >
          <ShapeSvg shapeKey={shapeKey} />
          <TitleText sx={{ mt: 1 }}>
            {t(`company_onboarding.modules.sites.shape_guide.${shapeKey}.title`)}
          </TitleText>
          <SecondaryText sx={{ mt: 0.5 }}>
            {t(`company_onboarding.modules.sites.shape_guide.${shapeKey}.body`)}
          </SecondaryText>
        </OnboardingCard>
      ))}
    </Box>
  </Box>
);

export default GeofenceShapeGuide;
